/**
 * Base64 (RFC 4648, with padding) for typed arrays, implemented without `btoa` or `Buffer` so it runs
 * unchanged in browsers and on the server. Multi-byte typed arrays are encoded by their little-endian bytes
 * on every host, so serialised documents are identical regardless of machine endianness.
 */

/**
 * Any typed array: a byte view with an element count and element width.
 */
export type TypedArray = ArrayBufferView & { readonly length: number; readonly BYTES_PER_ELEMENT: number };

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
const PAD = "=";
const VALUE_OF = new Int16Array(128).fill(-1);
for (let i = 0; i < ALPHABET.length; i += 1) {
  VALUE_OF[ALPHABET.charCodeAt(i)] = i;
}

/**
 * Returns the little-endian byte view of any typed array, swapping element bytes on big-endian hosts.
 *
 * @param data Typed array of any element width.
 * @param hostLittleEndian Whether the host stores multi-byte integers little-endian; defaults to detection.
 * @returns A `Uint8Array` over the same bytes in little-endian order.
 */
export function littleEndianBytes(data: TypedArray, hostLittleEndian: boolean = HOST_LITTLE_ENDIAN): Uint8Array {
  const raw = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  const width = data.BYTES_PER_ELEMENT;
  if (width <= 1 || hostLittleEndian) {
    return raw;
  }
  const swapped = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += width) {
    for (let j = 0; j < width; j += 1) {
      swapped[i + j] = raw[i + width - 1 - j] as number;
    }
  }
  return swapped;
}

/**
 * Whether the host stores multi-byte integers little-endian, detected once at module load.
 */
const HOST_LITTLE_ENDIAN = new Uint8Array(new Uint16Array([1]).buffer)[0] === 1;

/**
 * Encodes a typed array as padded base64.
 *
 * @param data Typed array to encode; multi-byte elements are encoded little-endian.
 * @returns The base64 text.
 */
export function encodeBase64(data: TypedArray): string {
  const bytes = littleEndianBytes(data);
  let out = "";
  let i = 0;
  for (; i + 2 < bytes.length; i += 3) {
    const triple = ((bytes[i] as number) << 16) | ((bytes[i + 1] as number) << 8) | (bytes[i + 2] as number);
    out += ALPHABET[(triple >>> 18) & 63]! + ALPHABET[(triple >>> 12) & 63]! + ALPHABET[(triple >>> 6) & 63]! + ALPHABET[triple & 63]!;
  }
  const remaining = bytes.length - i;
  if (remaining === 1) {
    const value = (bytes[i] as number) << 16;
    out += ALPHABET[(value >>> 18) & 63]! + ALPHABET[(value >>> 12) & 63]! + PAD + PAD;
  } else if (remaining === 2) {
    const value = ((bytes[i] as number) << 16) | ((bytes[i + 1] as number) << 8);
    out += ALPHABET[(value >>> 18) & 63]! + ALPHABET[(value >>> 12) & 63]! + ALPHABET[(value >>> 6) & 63]! + PAD;
  }
  return out;
}

/**
 * Decodes padded base64 text to bytes.
 *
 * @param text Base64 text as produced by `encodeBase64`.
 * @returns The decoded bytes.
 * @throws Error when the text is not valid padded base64.
 */
export function decodeBase64(text: string): Uint8Array {
  if (text.length % 4 !== 0) {
    throw new Error("invalid base64: length is not a multiple of four");
  }
  let padding = 0;
  if (text.endsWith(PAD + PAD)) {
    padding = 2;
  } else if (text.endsWith(PAD)) {
    padding = 1;
  }
  const bytes = new Uint8Array((text.length / 4) * 3 - padding);
  let outIndex = 0;
  for (let i = 0; i < text.length; i += 4) {
    let triple = 0;
    for (let j = 0; j < 4; j += 1) {
      const code = text.charCodeAt(i + j);
      if (i + j >= text.length - padding) {
        triple <<= 6;
        continue;
      }
      const value = code < 128 ? (VALUE_OF[code] as number) : -1;
      if (value < 0) {
        throw new Error(`invalid base64: unexpected character at ${i + j}`);
      }
      triple = (triple << 6) | value;
    }
    const produced = i + 4 > text.length - padding ? 3 - padding : 3;
    for (let k = 0; k < produced; k += 1) {
      bytes[outIndex] = (triple >>> (16 - 8 * k)) & 0xff;
      outIndex += 1;
    }
  }
  return bytes;
}
